/**
 * 🎯 V190.4: Unified Subscription Service
 * V213.1: Paddle removed - Google Play only on Android
 * 
 * Single source of truth for subscription status
 *
 * Location: src/services/subscription/UnifiedSubscriptionService.ts
 *
 * Usage:
 * import { UnifiedSubscriptionService } from '@/services/subscription/UnifiedSubscriptionService';
 *
 * const isVerified = await UnifiedSubscriptionService.isUserVerified(userId);
 */

// V242.2: Use baseSupabase (untracked) to bypass Proxy and prevent ".eq is not a function" errors
import { baseSupabase as supabase } from '@/integrations/supabase/client';
import type { Database } from '@/types/supabase';
import { VERIFIED_PRODUCT_CODE } from '@/config/subscriptions';
import type { SubscriptionStatus } from '@/billing/types';

type SubscriptionRow = Database['public']['Tables']['user_subscriptions']['Row'];
type SubscriptionInsert = Database['public']['Tables']['user_subscriptions']['Insert'];
type SubscriptionUpdate = Database['public']['Tables']['user_subscriptions']['Update'];

export type SubscriptionProvider = 'google_play' | 'apple';
// Re-export SubscriptionStatus from billing/types for consistency
export type { SubscriptionStatus };

export interface Subscription {
  id: string;
  userId: string;
  provider: SubscriptionProvider;
  providerSubscriptionId: string;
  productCode: string;
  status: SubscriptionStatus;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  canceledAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export class UnifiedSubscriptionService {
  private static readonly SERVICE_TAG = '🎯 V183.4: UnifiedSubscriptionService';

  /**
   * V210.1: Canonical subscription state resolver
   * Determines if a subscription is valid and grants access
   * 
   * CANONICAL RULES (NOW access):
   * 
   * HAS ACCESS (if currentPeriodEnd > now):
   * - 'active': Active subscription, grants access
   * - 'trialing': Trial period, grants access
   * - 'grace_period': Payment failed but still in grace, grants access
   * - 'canceled': Auto-renew off but still paid until period end, grants access
   * 
   * NO ACCESS (even if currentPeriodEnd > now):
   * - 'on_hold': Payment failed, grace expired, NO access
   * - 'paused': User-initiated pause, NO access
   * - 'expired': Subscription expired, NO access
   * - 'past_due': Payment failed (legacy), NO access
   * 
   * HARD RULE: If currentPeriodEnd <= now => NO ACCESS regardless of status
   */
  static resolveSubscriptionState(subscription: Subscription | null): {
    isValid: boolean;
    hasAccess: boolean;
    status: SubscriptionStatus | null;
    reason?: string;
  } {
    if (!subscription) {
      return {
        isValid: false,
        hasAccess: false,
        status: null,
        reason: 'No subscription found',
      };
    }

    const now = new Date();
    const periodEnd = new Date(subscription.currentPeriodEnd);
    const isPeriodExpired = periodEnd <= now;

    // HARD RULE: Period expired => NO ACCESS regardless of status
    if (isPeriodExpired) {
      return {
        isValid: false,
        hasAccess: false,
        status: subscription.status,
        reason: 'Subscription period has ended',
      };
    }

    // Status-based access rules (only evaluated if period not expired)
    switch (subscription.status) {
      case 'active':
      case 'trialing':
        return {
          isValid: true,
          hasAccess: true,
          status: subscription.status,
        };

      case 'grace_period':
        // Grace period: Payment failed but user still has access (within grace)
        return {
          isValid: true,
          hasAccess: true,
          status: subscription.status,
          reason: 'Payment issue - subscription still active in grace period',
        };

      case 'canceled':
        // Canceled: Auto-renew turned off but still paid until currentPeriodEnd
        // User has access until period end (real subscription semantics)
        return {
          isValid: true,
          hasAccess: true,
          status: subscription.status,
          reason: 'Subscription canceled (non-renewing) but access continues until period end',
        };

      case 'on_hold':
      case 'past_due':
        // On hold / Past due: Payment failed, grace period expired, NO access
        return {
          isValid: false,
          hasAccess: false,
          status: subscription.status,
          reason: 'Payment failed - subscription on hold',
        };

      case 'paused':
        // Paused: User-initiated pause, NO access
        return {
          isValid: false,
          hasAccess: false,
          status: subscription.status,
          reason: 'Subscription is paused',
        };

      case 'expired':
        return {
          isValid: false,
          hasAccess: false,
          status: subscription.status,
          reason: 'Subscription has expired',
        };

      default:
        // Unknown status - deny access for safety
        return {
          isValid: false,
          hasAccess: false,
          status: subscription.status,
          reason: `Unknown subscription status: ${subscription.status}`,
        };
    }
  }

  /**
   * V210.1: Check if user has active verified subscription from ANY provider
   * Uses canonical state resolver for consistent logic
   */
  static async isUserVerified(userId: string): Promise<boolean> {
    console.log(`${this.SERVICE_TAG} - Checking verification status for user ${userId}`);

    try {
      const subscription = await this.getValidSubscription(userId, VERIFIED_PRODUCT_CODE);
      
      if (!subscription) {
        console.log(`${this.SERVICE_TAG} - User ${userId} not verified: no valid subscription`);
        return false;
      }

      const state = this.resolveSubscriptionState(subscription);
      const isVerified = state.hasAccess;

      console.log(`${this.SERVICE_TAG} - User ${userId} verified: ${isVerified} (${subscription.provider}, ${subscription.status})${state.reason ? ` - ${state.reason}` : ''}`);
      return isVerified;
    } catch (error) {
      console.error(`${this.SERVICE_TAG} - Error checking verification:`, error);
      return false;
    }
  }

  /**
   * V210.1: Get valid subscription (checks up to 5 most recent rows)
   * Returns first subscription that grants access, null otherwise
   * Uses canonical state resolver to determine validity
   * 
   * Why check multiple rows: User might have newer canceled/paused subscription
   * while an older active subscription still grants access
   */
  static async getValidSubscription(
    userId: string,
    productCode: string
  ): Promise<Subscription | null> {
    // V242: Defensive guard - if userId or productCode missing, return null silently
    if (!userId || !productCode) {
      console.log(`${this.SERVICE_TAG} - Missing userId or productCode, returning null`);
      return null;
    }

    console.log(`${this.SERVICE_TAG} - Getting valid subscription for user ${userId}, product ${productCode}`);

    try {
      // V242: Wrap in try/catch to prevent any query builder errors from crashing
      // Query up to 5 most recent subscriptions (any status)
      // We'll evaluate each using canonical state resolver
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('product_code', productCode)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        console.error(`${this.SERVICE_TAG} - Error querying subscription:`, error);
        return null;
      }

      if (!data || data.length === 0) {
        console.log(`${this.SERVICE_TAG} - No subscription found`);
        return null;
      }

      // Evaluate each subscription using canonical state resolver
      // Return first one that grants access
      for (const row of data) {
        const subscription = this.mapToSubscription(row);
        const state = this.resolveSubscriptionState(subscription);

        if (state.hasAccess) {
          console.log(`${this.SERVICE_TAG} - Found valid subscription from ${subscription.provider} (${subscription.status})`);
          return subscription;
        }
      }

      // No subscription grants access
      const firstState = this.resolveSubscriptionState(this.mapToSubscription(data[0]));
      console.log(`${this.SERVICE_TAG} - No valid subscription found. Latest: ${data[0].status} - ${firstState.reason || 'no access'}`);
      return null;
    } catch (err) {
      // V242: Catch ANY error including query builder issues like "eq is not a function"
      console.error(`${this.SERVICE_TAG} - Unexpected error in getValidSubscription:`, err);
      return null;
    }
  }

  /**
   * Get active subscription for user and product
   * Returns null if no active subscription found
   * @deprecated Use getValidSubscription() instead for canonical state resolution
   */
  static async getActiveSubscription(
    userId: string,
    productCode: string
  ): Promise<Subscription | null> {
    console.log(`${this.SERVICE_TAG} - [DEPRECATED] getActiveSubscription() called - use getValidSubscription() instead`);
    return this.getValidSubscription(userId, productCode);
  }

  /**
   * V210.1: Get current user's valid subscription (convenience method)
   * Checks for verified product by default
   * Returns null if no valid subscription found (uses canonical state resolver)
   */
  static async getCurrentSubscription(userId?: string): Promise<Subscription | null> {
    if (!userId) {
      console.log(`${this.SERVICE_TAG} - No userId provided to getCurrentSubscription`);
      return null;
    }

    console.log(`${this.SERVICE_TAG} - Getting current subscription for user ${userId}`);
    return this.getValidSubscription(userId, VERIFIED_PRODUCT_CODE); // V210.1: Use canonical state resolver
  }

  /**
   * Get all subscriptions for user (any status, any provider)
   * Used for subscription management screens
   */
  static async getUserSubscriptions(userId: string): Promise<Subscription[]> {
    console.log(`${this.SERVICE_TAG} - Getting all subscriptions for user ${userId}`);

    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error(`${this.SERVICE_TAG} - Error querying subscriptions:`, error);
      return [];
    }

    console.log(`${this.SERVICE_TAG} - Found ${data.length} subscriptions`);
    return data.map(this.mapToSubscription);
  }

  /**
   * Upsert subscription (webhook handlers use this)
   * Creates new or updates existing subscription
   */
  static async upsertSubscription(subscription: SubscriptionInsert): Promise<boolean> {
    console.log(`${this.SERVICE_TAG} - Upserting subscription for user ${subscription.user_id}, provider ${subscription.provider}`);

    const { error } = await supabase
      .from('user_subscriptions')
      .upsert(subscription, {
        onConflict: 'user_id,provider,product_code',
      });

    if (error) {
      console.error(`${this.SERVICE_TAG} - Error upserting subscription:`, error);
      return false;
    }

    console.log(`${this.SERVICE_TAG} - Subscription upserted successfully`);
    return true;
  }

  /**
   * Update subscription status
   * Used by webhook handlers to sync status changes
   */
  static async updateSubscriptionStatus(
    userId: string,
    provider: SubscriptionProvider,
    productCode: string,
    updates: SubscriptionUpdate
  ): Promise<boolean> {
    console.log(`${this.SERVICE_TAG} - Updating subscription status for user ${userId}, provider ${provider}`);

    const { error } = await supabase
      .from('user_subscriptions')
      .update(updates)
      .eq('user_id', userId)
      .eq('provider', provider)
      .eq('product_code', productCode);

    if (error) {
      console.error(`${this.SERVICE_TAG} - Error updating subscription:`, error);
      return false;
    }

    console.log(`${this.SERVICE_TAG} - Subscription updated successfully`);
    return true;
  }

  /**
   * Cancel subscription (sets cancel_at_period_end = true)
   * User retains access until end of billing period
   */
  static async cancelSubscription(
    userId: string,
    provider: SubscriptionProvider,
    productCode: string
  ): Promise<boolean> {
    console.log(`${this.SERVICE_TAG} - Canceling ${provider} subscription for user ${userId}`);

    const { error } = await supabase
      .from('user_subscriptions')
      .update({
        cancel_at_period_end: true,
        canceled_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId)
      .eq('provider', provider)
      .eq('product_code', productCode);

    if (error) {
      console.error(`${this.SERVICE_TAG} - Error canceling subscription:`, error);
      return false;
    }

    console.log(`${this.SERVICE_TAG} - Subscription canceled successfully (ends at period end)`);
    return true;
  }

  /**
   * Get subscription by provider subscription ID
   * Used by webhooks to find subscription
   */
  static async getSubscriptionByProviderId(
    providerSubscriptionId: string
  ): Promise<Subscription | null> {
    console.log(`${this.SERVICE_TAG} - Getting subscription by provider ID: ${providerSubscriptionId}`);

    // 🎯 V184.1.1: Use maybeSingle() to prevent 406 errors when subscription not found
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('provider_subscription_id', providerSubscriptionId)
      .maybeSingle();

    if (error) {
      console.error(`${this.SERVICE_TAG} - Error querying subscription:`, error);
      return null;
    }

    if (!data) {
      console.log(`${this.SERVICE_TAG} - Subscription not found`);
      return null;
    }

    return this.mapToSubscription(data);
  }

  /**
   * Map database row to Subscription object
   */
  private static mapToSubscription(row: SubscriptionRow): Subscription {
    return {
      id: row.id,
      userId: row.user_id,
      provider: row.provider as SubscriptionProvider,
      providerSubscriptionId: row.provider_subscription_id,
      productCode: row.product_code,
      status: row.status as SubscriptionStatus,
      currentPeriodStart: new Date(row.current_period_start),
      currentPeriodEnd: new Date(row.current_period_end),
      cancelAtPeriodEnd: row.cancel_at_period_end,
      canceledAt: row.canceled_at ? new Date(row.canceled_at) : null,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
    };
  }
}

export default UnifiedSubscriptionService;

